
/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import moment from 'moment';

export class DateHelper {
    static readonly FMT = 'YYYY-MM-DD';

    static unixDatePlusDays(dt, days) : Date {
        if (dt && Math.abs(dt) > (24*60*60*1000)) { // Null or 1/1/1970 probably a mistake
            const newDt = new Date()
            newDt.setTime((dt * 1000) + (days * 24 * 60 * 60 * 1000));
            return newDt;
        } else {
            console.warn('datePlusDays called with invalid date', {dt, days});
            return new Date();
        }
    }

    static longDate(dateString: string = null): string {
        const dt = dateString ? new Date(dateString + 'T12:00:00') : new Date();
        return dt.toLocaleDateString('en-IE', { weekday: "long", month: "long", day: "numeric" });
    }
    static localDate(dateString: string): string {
        if (dateString) {
            const dt = new Date(dateString + 'T12:00:00');
            return dt.toLocaleDateString('en-IE', { month: "short", day: "numeric", year: 'numeric' });
        } else {
            return '';
        }
    }
    static localDateTime(value) {
        const dt = new Date(value * 1000);
        return dt.toLocaleDateString('en-IE',
            { month: "short", day: "numeric", year: 'numeric', hour: '2-digit', minute: '2-digit' }
        );
    }

    static isoFromDDMMYY(ddMMyy: string) {
        const day = ddMMyy.substring(0,2);
        const month = ddMMyy.substring(3,5);
        const year = ddMMyy.substring(6);
        return `${year}-${month}-${day}`
    }

    static isoFromDDMMMYYYY(dateStr: string) {
        const months: Record<string, string> = {
            JAN: "01", FEB: "02", MAR: "03", APR: "04",
            MAY: "05", JUN: "06", JUL: "07", AUG: "08",
            SEP: "09", OCT: "10", NOV: "11", DEC: "12"
        };

        const day = dateStr.slice(0, 2);
        const monStr = dateStr.slice(2, 5).toUpperCase();
        const year = dateStr.slice(5);

        const month = months[monStr];

        if (!month || +day > 31 || +day <=0 || +year <= 1900 || +year > 2099) {
            console.log(`${dateStr} not a valid date in DDMMMYYYY format`);
            return null;
        }

        return `${year}-${month}-${day}`;
    }

    static isValidISODate(dateString: string): boolean {
        // 1. Check the format with a regular expression
        //    ^\\d{4}   - Start of string, exactly 4 digits for the year
        //    -        - Literal hyphen
        //    \\d{2}   - Exactly 2 digits for the month
        //    -        - Literal hyphen
        //    \\d{2}$  - Exactly 2 digits for the day, end of string
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dateString)) {
            return false; // Format is incorrect
        }

        // 2. Attempt to create a Date object
        //    The Date constructor can parse YYYY-MM-DD strings.
        //    However, it's lenient and might "correct" invalid dates (e.g., Feb 30 becomes Mar 2).
        const date = new Date(dateString);

        // 3. Check if the Date object is valid AND if its string representation
        //    (in ISO format, which is YYYY-MM-DDTHH:mm:ss.sssZ)
        //    starts with the original input string. This ensures the date wasn't "corrected".
        //    For example, new Date("2023-02-30").toISOString() might be "2023-03-02T..."
        //    The slice(0, 10) extracts just the YYYY-MM-DD part.
        return !isNaN(date.getTime()) && date.toISOString().slice(0, 10) === dateString;
    }

    static dateFromISO(yyyy_mm_hh: string): Date {
        return new Date(yyyy_mm_hh);
    }

    static isWeekend(yyyy_mm_hh: string): boolean {
        const dow = this.dateFromISO(yyyy_mm_hh).getDay();
        return (dow === 0 || dow === 6) ? true : false;
    }

    static today(adjustDays = 0) {
        const dt = new Date();
        dt.setDate(dt.getDate() + adjustDays);
        return DateHelper.isoFromCalendarDate(dt);
    }

    static daysFromToday(dateStr: string): number {
        const inputDate = new Date(dateStr);
        const today = new Date();

        // Zero out time for accurate comparison
        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const msPerDay = 1000 * 60 * 60 * 24;
        const diffMs = inputDate.getTime() - today.getTime();

        return Math.round(diffMs / msPerDay);
    }

    static secondsFromToday(unixTimeInSeconds: number): number {
        const now = Math.floor(Date.now() / 1000); // current time in seconds
        return unixTimeInSeconds - now;
    }    

    static isoDate(dt: Date = new Date()) {
        return dt.toISOString().slice(0, 10);
    }

    /** Unlike isoDate(), reads the calendar date the user sees, so a local midnight never
     *  slips back a day when the browser is ahead of UTC. */
    static isoFromCalendarDate(calendarDate: Date): string {
        const year = calendarDate.getFullYear();
        const month = String(calendarDate.getMonth() + 1).padStart(2, '0');
        const day = String(calendarDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    static calendarDateFromIso(yyyyMmDd: string): Date | null {
        if (!DateHelper.isValidISODate(yyyyMmDd)) {
            return null;
        }
        const [year, month, day] = yyyyMmDd.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    /**
     * Test if a date is between two other dates, assuming all dates in ISO string format
     * @param theDate - The date to be tested
     * @param startDate - Start Date inclusive
     * @param endDate - End Date inclusive
     * @returns
     */

    static isoDateIn(theDate: string | Date, startDate: string, endDate: string) {
        let momDate: moment.Moment;
        if (typeof theDate === 'string' || theDate instanceof String) {
            momDate = moment(theDate, DateHelper.FMT);
        } else {
            momDate = moment(theDate);
        }

        if (momDate.isBetween(
            moment(startDate, DateHelper.FMT),
            moment(endDate, DateHelper.FMT), 'days', '[]')) {
            return true;
        } else {
            return false;
        }
    }
    static isoIncludes(theDate: string, startDate: string, endDate: string) {
        if (moment(theDate, DateHelper.FMT).isBetween(
            moment(startDate, DateHelper.FMT),
            moment(endDate, DateHelper.FMT), 'days', '[]')) {
            return true;
        } else {
            return false;
        }
    }

    static isoDaysSince(theDate: string): number {
        return moment().diff(moment(theDate, DateHelper.FMT), 'days');
        //return 0;
    }
    static isoDaysTo(theDate: string): number {
        return moment(theDate, DateHelper.FMT).diff(moment(), 'days');
        //return 0;
    }
    static isoDaysBetween(dateFrom: string, dateTo: string): number {
        return moment(dateTo, DateHelper.FMT).diff(moment(dateFrom, DateHelper.FMT), 'days');
    }

    static addDays(dateFrom: string, add: number): string { // little testing...
        //const out = DateHelper.dateFromISO(dateFrom);
        //out.setDate(out.getDate() + add);
        //const outISO = DateHelper.isoDate(out);

        const newDate = moment(dateFrom, 'YYYY-MM-DD');
        const offsetDate = newDate.add(add, 'days');
        return offsetDate.format('YYYY-MM-DD');
    }

    static adjustDate(from: string, days: number) {
        return DateHelper.addDays(from, days);
    }

    static beforeToday(value: string) {
        if (moment(value, 'YYYY-MM-DD').isBefore(new Date().setDate(new Date().getDate() - 1))) {
            return true;
        } else {
            return false;
        }
    }

    static dayAfter(dateFrom: string): string {
        return DateHelper.addDays(dateFrom, 1);
    }

}
